import React, { useEffect, useState } from 'react';
import { 
  SparklesIcon,
  ClockIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import { getResourceBySlug } from '../../../lib/db';

const KeyInfo = () => {
  const [resource, setResource] = useState(null);

  useEffect(() => {
    getResourceBySlug('leverage-ai-for-you-and-your-org').then(setResource);
  }, []);

  return (
    <div>
      <PlaybookHero
        title={resource?.title || "How To Leverage AI For Your Team, Yourself And Your Org"}
        subtitle={resource?.description || "A simple but powerful framework for spotting AI use cases, building your first GPT agents, and scaling AI confidence across your organization"}
        category={resource?.category || "AI & Technology"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "15 min read"}
        type={resource?.type}
      />
      <div id="key-info" className="prose prose-invert max-w-none">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <LightBulbIcon className="h-6 w-6 text-primary-light mr-2" />
          Key Info
        </h2>
        
        {/* Core Principles Quote */}
        <div className="border-l-4 border-primary-light pl-6 italic text-gray-300 p-6 border border-gray-800 rounded-lg bg-gray-900/50 mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">Core Principles Behind This Playbook</h3>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span><strong className="text-white">Speed over perfection:</strong> Learn by doing, not theorising.</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span><strong className="text-white">Empowerment over policing:</strong> People adopt what they help build.</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span><strong className="text-white">Career relevance over compliance:</strong> Link AI to personal growth to unlock real adoption.</span>
            </li>
          </ul>
        </div>

        {/* This Guide is for you if */}
        <h3 className="text-xl font-semibold mb-4">This Guide is for you if any of the following are true:</h3>
        <ul className="space-y-3 mb-8">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You've been asked to "make us more AI fluent" and don't know where to start.</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You want to help your org use AI without getting stuck in hype or compliance.</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You need lightweight, low-lift ways to scale confidence and competence fast.</span>
          </li>
        </ul>

        {/* Guide Overview */}
        <h3 className="text-xl font-semibold mb-4">Guide Overview</h3>
        <div className="mb-12">
          <PlaybookMetrics 
            metrics={[
              {
                label: 'Timeline',
                value: '4-8 Hours Prep'
              },
              {
                label: 'Team Size',
                value: 'All'
              },
              {
                label: 'Why Use',
                value: 'Boost team fluency, productivity and confidence with AI'
              },
              {
                label: 'When to Use',
                value: 'New tools launched, team overwhelmed, or leadership pushing for \'AI adoption\''
              },
              {
                label: 'Difficulty',
                value: 'Medium-High'
              }
            ]}
          />
        </div>

        {/* Watch This section */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <PlayIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Watch This</h3>
          </div>
         
          <VideoEmbed
            src="https://www.loom.com/embed/57c457afa9d248d1a9c83d2c804225f0"
            title="Building a custom GPT agent from scratch"
            caption="Walkthrough: overview of this playbook"
          />
        </div>

        {/* What Is This Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <LightBulbIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What Is This</h3>
          </div>
          <div className="space-y-4">
            <p className="text-lg">
              This playbook provides a practical, step-by-step approach to implementing AI across your organization without getting overwhelmed by hype or bogged down in compliance.
            </p>
            <p className="text-lg">
              You'll learn how to identify high-impact AI opportunities, build your first custom GPT agents, and create sustainable adoption practices that empower your team rather than create fear.
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
              <h4 className="font-semibold text-white mb-3">What you'll leave with:</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>A simple but powerful framework for spotting AI use cases across your org</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>A reusable prompt template for building your first GPT agents</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Confidence to prototype and launch useful AI tools for your people team, fast</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* What You'll Need Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <UserGroupIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What You'll Need</h3>
          </div>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-white mb-2">Required Tools</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>ChatGPT PRO subscription ($20/month)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Bolt.new or Lovable.com PRO ($20/month) for UI prototyping</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Miro or similar collaboration tool for team workshops</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-2">Team Requirements</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Basic understanding of your team's repetitive tasks and pain points</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Willingness to experiment and iterate quickly</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <span>Alignment with Tech/Security on basic AI usage guidelines</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyInfo; 